import { useEffect, useMemo, type FC } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents, urlTransform } from "../../../utils/markdownComponents";
import type { CalendarData, CalendarDayData } from "../../../core/types";
import { RichMediaScope } from "../../../contexts/RichMediaContext";
import { useScroll } from "../../../contexts/useScroll";

interface CalendarMessageProps {
  messageId: number;
  threadId: number;
  isTyping: boolean;
  data: CalendarData;
  onSlotSelect: (message: string) => void;
  disabled?: boolean;
  message?: string;
}

const getLocale = (lang: string): string => {
  if (lang === "fi") return "fi-FI";
  if (lang === "sv") return "sv-SE";
  return "en-US";
};

const getWeekdayNames = (locale: string): string[] => {
  // 2024-01-01 is a known Monday
  const monday = new Date("2024-01-01T00:00:00");
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toLocaleDateString(locale, { weekday: "narrow" });
  });
};

const getMonthLabel = (weekMonday: Date, lang: string, locale: string): string => {
  const weekSunday = new Date(weekMonday);
  weekSunday.setDate(weekMonday.getDate() + 6);

  const startMonth = weekMonday.toLocaleDateString(locale, { month: "long" });
  const endMonth = weekSunday.toLocaleDateString(locale, { month: "long" });
  const year = weekSunday.getFullYear();

  // Finnish month names are in genitive in date contexts but nominative as headers
  if (lang === "fi") {
    const fiMonths = ["Tammikuu","Helmikuu","Maaliskuu","Huhtikuu","Toukokuu","Kesäkuu","Heinäkuu","Elokuu","Syyskuu","Lokakuu","Marraskuu","Joulukuu"];
    const startFi = fiMonths[weekMonday.getMonth()];
    const endFi = fiMonths[weekSunday.getMonth()];
    return startFi === endFi ? `${startFi} ${year}` : `${startFi} – ${endFi} ${year}`;
  }
  return startMonth === endMonth ? `${startMonth} ${year}` : `${startMonth} – ${endMonth} ${year}`;
};

const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const getWeekLabel = (date: Date, lang: string): string => {
  const n = getISOWeekNumber(date);
  if (lang === "fi") return `vko ${n}`;
  if (lang === "sv") return `v. ${n}`;
  return `Week ${n}`;
};

const getPromptText = (lang: string): string => {
  if (lang === "fi") return "Valitse sinulle sopiva aika";
  if (lang === "sv") return "Välj en tid som passar dig";
  return "Select a time that works for you";
};

// The transport has been observed to omit `days` entirely on a malformed
// payload. Normalise here as well as at the render sites: a throw from the
// fence helpers would take down the whole message subtree, not just the grid.
const calendarDays = (data: CalendarData): CalendarDayData[] => data.days ?? [];

const calendarDayRow = (data: CalendarData): string | null => {
  const days = calendarDays(data);
  if (days.length === 0) return null;
  const dayNumbers = days.map((day) => new Date(`${day.date}T00:00:00`).getDate().toString());
  return dayNumbers.map((dayNumber) => dayNumber.padEnd(6, " ")).join("");
};

const hasMostlyNumericRow = (lines: string[]): boolean =>
  lines.some((line) => {
    const nonWhitespace = line.replace(/\s/g, "");
    const digitCount = (nonWhitespace.match(/\d/g) ?? []).length;
    return digitCount >= 3 && digitCount / nonWhitespace.length >= 0.8;
  });

const isTextCalendarFence = (body: string, data: CalendarData): boolean => {
  const days = calendarDays(data);
  const expectedRow = calendarDayRow(data);
  const lines = body.split(/\r?\n/);
  // No payload dates to match against: fall back to the shape heuristic.
  if (expectedRow === null || days.length === 0) return hasMostlyNumericRow(lines);
  const calendarYear = days[days.length - 1].date.slice(0, 4);
  // The transport's fixed-width seven-column row comes directly from
  // CalendarData.days[].date. Unlike weekday labels, it is locale-independent
  // and cannot match a prose opening-hours table by a substring. The preceding
  // month/year and header lines are part of the transport's calendar shape.
  const matchesPayloadDates = lines.some((line, index) =>
    index >= 2 &&
    line.trimEnd() === expectedRow.trimEnd() &&
    lines[index - 2].includes(calendarYear) &&
    lines[index - 1].trim().length > 0,
  );
  // If a transport formatting change makes the payload row unrecognisable,
  // prefer hiding a bare calendar-like table over rendering its duplicate above
  // the interactive grid. The strict payload match remains the normal path.
  return matchesPayloadDates || hasMostlyNumericRow(lines);
};

interface FenceStart {
  start: number;
  lineEnd: number;
  contentStart: number;
  marker: string;
}

/**
 * The Calendly transport sends an unlabelled text fallback. Pair fences before
 * considering removal so a closer is never treated as an opener. A recovery
 * branch recognises the calendar that follows a truncated lastbot-media block.
 */
const stripTextCalendarFence = (contents: string, data: CalendarData): string => {
  const fenceLines = /^```([^\r\n]*)\r?$/gm;
  const removals: Array<{ start: number; end: number }> = [];
  const fences: FenceStart[] = [];
  let match: RegExpExecArray | null;

  while ((match = fenceLines.exec(contents)) !== null) {
    const lineEnd = match.index + match[0].length;
    fences.push({
      start: match.index,
      lineEnd,
      contentStart: lineEnd + (contents[lineEnd] === "\n" ? 1 : 0),
      marker: match[1].trim(),
    });
  }

  let openFence: FenceStart | null = null;
  for (let index = 0; index < fences.length; index += 1) {
    const fence = fences[index];
    if (openFence === null) {
      openFence = fence;
      continue;
    }

    const body = contents.slice(openFence.contentStart, fence.start);
    if (openFence.marker.length === 0 && isTextCalendarFence(body, data)) {
      removals.push({ start: openFence.start, end: fence.lineEnd });
    } else if (openFence.marker === "lastbot-media" && fence.marker.length === 0) {
      const nextFence = fences[index + 1];
      if (nextFence?.marker.length === 0) {
        const calendarBody = contents.slice(fence.contentStart, nextFence.start);
        if (isTextCalendarFence(calendarBody, data)) {
          // `fence` is the media closer under Markdown's pairing rules. Keep it
          // and remove only the raw calendar plus its dangling closer.
          removals.push({ start: fence.contentStart, end: nextFence.lineEnd });
          index += 1;
        }
      }
    }
    openFence = null;
  }

  if (openFence?.marker.length === 0 && isTextCalendarFence(contents.slice(openFence.contentStart), data)) {
    removals.push({ start: openFence.start, end: contents.length });
  }

  return removals
    .reverse()
    .reduce((result, removal) => `${result.slice(0, removal.start)}${result.slice(removal.end)}`, contents)
    .trim();
};

// The booking zone arrives as an IANA name from the server. An older server
// omits it and a malformed one must not break the grid, so anything `Intl`
// rejects resolves to undefined and the caller falls back to browser-local time.
const resolveTimeZone = (timeZone: string | undefined): string | undefined => {
  if (typeof timeZone !== "string" || timeZone.trim() === "") return undefined;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return undefined;
  }
};

const formatChipTime = (isoStr: string, lang: string, timeZone?: string): string => {
  const d = new Date(isoStr);
  let h: string;
  let m: string;
  if (timeZone) {
    // h23 keeps midnight as 0 (some engines print "24" with hour12: false).
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(d);
    h = String(Number(parts.find((part) => part.type === "hour")?.value ?? "0"));
    m = parts.find((part) => part.type === "minute")?.value ?? "00";
  } else {
    h = d.getHours().toString();
    m = d.getMinutes().toString().padStart(2, "0");
  }
  if (lang === "fi") return `${h}.${m}`;
  return `${h}:${m}`;
};

const buildSlotMessage = (isoStr: string, lang: string, locale: string, timeZone?: string): string => {
  const date = new Date(isoStr);
  const zone = timeZone ? { timeZone } : {};
  const dateStr = date.toLocaleDateString(locale, { day: "numeric", month: "long", ...zone });
  const timeStr = date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit", ...zone });
  // Include the raw ISO string so the bot can use it directly as start_time
  // for schedule_meeting without guessing from the locale-formatted display time.
  const human = lang === "fi" ? `${dateStr} klo ${timeStr}` :
                lang === "sv" ? `${dateStr} kl. ${timeStr}` :
                `${dateStr} at ${timeStr}`;
  return `${human} [${isoStr}]`;
};

const CalendarMessage: FC<CalendarMessageProps> = ({ messageId, threadId, isTyping, data, onSlotSelect, disabled = false, message }) => {
  const lang = data.lang || "en";
  const locale = getLocale(lang);
  const timeZone = resolveTimeZone(data.timezone);
  const weekdayNames = getWeekdayNames(locale);

  // Parse week_monday as local date to avoid UTC-offset day-shift
  const weekMonday = new Date(data.week_monday + "T00:00:00");
  const monthLabel = getMonthLabel(weekMonday, lang, locale);

  // Day numbers for each column header
  const dayNumbers = calendarDays(data).map((d) => new Date(d.date + "T00:00:00").getDate().toString());

  // Per-column slots, displayed in the booking zone (browser-local if none)
  const daySlots = calendarDays(data).map((d) =>
    (d.slots ?? []).map((isoStr) => ({
      iso: isoStr,
      display: formatChipTime(isoStr, lang, timeZone),
    }))
  );

  const maxRows = Math.max(...daySlots.map((s) => s.length), 0);
  const promptText = getPromptText(lang);

  const handleClick = (isoStr: string): void => {
    if (disabled) return;
    onSlotSelect(buildSlotMessage(isoStr, lang, locale, timeZone));
  };

  // The backend keeps its text-calendar fallback in `contents` for text channels
  // (WhatsApp, etc.), but this interactive grid replaces only that fence.
  // The fence regex walks the whole reply, and `message` changes on every
  // streaming token, so keep it off the per-token render path.
  const prose = useMemo(() => stripTextCalendarFence(message ?? "", data), [message, data]);

  const { keepAnchor } = useScroll();
  useEffect(() => {
    // The grid replaces the streamed reply once its calendar metadata lands,
    // without a typewriter tick to re-apply the reading anchor the reply was
    // given — so re-apply it here (or follow the bottom where the list chose
    // that), deferred until the grid has laid out (LAS-1381).
    const timer = window.setTimeout(keepAnchor, 0);
    return (): void => window.clearTimeout(timer);
  }, [keepAnchor]);

  return (
    <RichMediaScope threadId={threadId} messageId={messageId} settled={!isTyping}>
    <div
      className="w-full rounded-xl bg-lastbot-surface-subtle border border-lastbot-border-strong p-4"
      data-message-id={messageId}
    >
      {prose && (
        <div className="leading-[1.6] text-lastbot-on-surface mb-3 formatted-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={urlTransform}>
            {prose}
          </ReactMarkdown>
        </div>
      )}
      {/* Header separated by a divider so June/week don't need to align with column headers */}
      <div className="flex items-baseline justify-between mb-4 pb-3 border-b border-lastbot-border-strong">
        <div className="text-xl font-bold text-lastbot-on-surface">{monthLabel}</div>
        <div className="text-base font-medium text-lastbot-on-surface-soft">{getWeekLabel(weekMonday, lang)}</div>
      </div>
      <div className="grid grid-cols-7 gap-x-1 gap-y-2">
        {weekdayNames.map((name, i) => (
          <div key={i} className="text-center text-sm font-semibold text-lastbot-on-surface-subtle pb-1 truncate">
            {name}
          </div>
        ))}
        {dayNumbers.map((num, i) => (
          <div key={i} className="text-center text-xl font-bold text-lastbot-on-surface pb-2">
            {num}
          </div>
        ))}
        {Array.from({ length: maxRows }, (_, rowIdx) =>
          daySlots.map((slots, colIdx) => {
            const slot = slots[rowIdx];
            return slot ? (
              <button
                key={`${rowIdx}-${colIdx}`}
                onClick={() => handleClick(slot.iso)}
                disabled={disabled}
                className="text-sm tabular-nums bg-lastbot-primary text-lastbot-on-primary rounded-md px-0.5 py-2 w-full text-center leading-none whitespace-nowrap overflow-hidden hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity mb-1"
              >
                {slot.display}
              </button>
            ) : (
              <div key={`${rowIdx}-${colIdx}`} className="mb-1" />
            );
          })
        )}
      </div>
      {maxRows > 0 && (
        <div className="text-sm text-lastbot-on-surface-subtle mt-4 pt-3 border-t border-lastbot-border">{promptText}</div>
      )}
    </div>
    </RichMediaScope>
  );
};

export default CalendarMessage;
